import React from "react";

const BellCurveInsights = ({ data }) => {

  if (!data || data.length === 0) return null;

const normalizeValue = (value) => {

    if (typeof value === "number") return value;

    if (typeof value === "string") {

      const trimmed = value.trim();

      /* numeric string */
      if (!isNaN(trimmed)) return Number(trimmed);

      /* time format */
      if (trimmed.includes(":")) {

        const [time, period] = trimmed.split(" ");
        const [hours, minutes] = time.split(":").map(Number);

        let totalMinutes = hours * 60 + minutes;

        if (period === "PM" && hours !== 12) totalMinutes += 720;
        if (period === "AM" && hours === 12) totalMinutes -= 720;

        return totalMinutes;
      }
    }

    return null;
  };

  // const values = data.map(Number).filter(v => !isNaN(v));
  const values = data
    .map(v => normalizeValue(v))
    .filter(v => v !== null);

  const n = values.length;

  const mean = values.reduce((a, b) => a + b, 0) / n;

  const sorted = [...values].sort((a, b) => a - b);

  const median =
    n % 2 === 0
      ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
      : sorted[Math.floor(n / 2)];

  const variance =
    values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;

  const stdDev = Math.sqrt(variance);

  const min = Math.min(...values);
  const max = Math.max(...values);

  const normalLow = mean - stdDev;
  const normalHigh = mean + stdDev;

  const outliers = values.filter(v => v < normalLow || v > normalHigh);

  /* ---------- CONSISTENCY SCORE ---------- */

  const coeffVariation = stdDev / mean;
  let consistencyScore = Math.max(
    0,
    Math.min(100, Math.round((1 - coeffVariation) * 100))
  );

  
  /* ---------- Variation Level ---------- */

  // let variation = "Stable";
  let variationLevel = "Stable";
  // if (stdDev > mean * 0.5) variation = "High Variation";
  // else if (stdDev > mean * 0.2) variation = "Moderate Variation";
  if (coeffVariation > 0.5) variationLevel = "High Variation";
  else if (coeffVariation > 0.2) variationLevel = "Moderate Variation";

/* ---------- AUTOMATIC INTERPRETATION ---------- */

  let interpretation = "";

  if (variationLevel === "Stable") {
    interpretation =
      "Most values cluster closely around the average. This indicates a stable and predictable process.";
  }

  if (variationLevel === "Moderate Variation") {
    interpretation =
      "The data shows noticeable variation around the average. Monitoring the process may help improve consistency.";
  }

  if (variationLevel === "High Variation") {
    interpretation =
      "The distribution shows large variation. Some values deviate significantly from the normal range. Investigating root causes may help stabilize the process.";
  }

  if (outliers.length > 0) {
    interpretation +=
      " A few extreme values were detected which may indicate unusual events or errors.";
  }

  return (
    <div className="mt-6 p-4 bg-gray-900 [&_p]:text-white rounded">

      <h3 className="text-lg text-yellow-300 mb-3">
        📊 Bell Curve Insights
      </h3>

      <div className="text-sm space-y-1">

        <div>Average (Mean): <span className="text-teal-300">{mean.toFixed(2)}</span></div>

        <div>Median: <span className="text-teal-300">{median.toFixed(2)}</span></div>

        <div>Standard Deviation: <span className="text-teal-300">{stdDev.toFixed(2)}</span></div>

        <div>Normal Range: 
          <span className="text-teal-300">
            {" "} {normalLow.toFixed(2)} – {normalHigh.toFixed(2)}
          </span>
        </div>

        <div>Variation Level: 
          <span className="text-yellow-300"> {variationLevel}</span>
        </div>

        <div>Outliers Detected: 
          <span className="text-red-400"> {outliers.length}</span>
        </div>

      </div>

      <div className="mt-4 text-sm text-gray-300">

        {variationLevel === "Stable" && (
          <p>
            Most values fall within a narrow range. The process appears stable
            and predictable.
          </p>
        )}

        {variationLevel === "Moderate Variation" && (
          <p>
            Some variation exists around the average. Monitoring the process
            may help reduce inconsistency.
          </p>
        )}

        {variationLevel === "High Variation" && (
          <p>
            The data shows large variation. Some values deviate significantly
            from the normal range. Investigating root causes may help improve
            stability.
          </p>
        )}

      </div>
      <div className="mt-4 text-gray-300">

        <strong>🧠 Interpretation</strong>

        <p className="mt-1">
          {interpretation}
        </p>

      </div>

    </div>
  );
};

export default BellCurveInsights;